import {type WCOrder} from './../../../../@type/woocommerce/order';
import {type AmazonGateway, type IAmazonInstance, type IAmazonPaySession, type IAmazonWindowMessage} from '../../models/IAmazon';
import {store} from '../../store';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {Environment, startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {MerchantConfiguration} from '../../reducers/merchantConfigurationReducer';
import {type ITransactionService, type OrderService} from '../order';
import {setupAmazonPayButton} from './button';
import {getBaseURL} from '../../util/environment';
import {DefaultCart} from '../../reducers/cartReducer';
import {PeachPayCustomer, updateCustomerPreferredGateway} from '../../reducers/peachPayCustomerReducer';
import {Carts} from '../../reducers/cartReducer';
import {updateCustomerDataInBrowser} from '../../features/customer';
import {clearLocalSession} from '../session';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {AmazonPaySession} from './session';
import {createPopupWindow, getAmazonPayCheckoutScript} from './utils';
import {loadScript, $qsAll, $qs, onWindowDataFetch, onWindowMessage} from '../../../../@shared/ts/dom';
import amazonBadgeURL from '../../../img/badge/amazon.svg';
import amazonImageURL from '../../../img/marks/amazon-pay-card.svg';
import {captureSentryException} from '../../../../@shared/ts/sentry';
import {GLOBAL} from '../../deprecated/global';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';

declare const amazon: IAmazonInstance;

export default async function initAmazonPaymentIntegration(orderService: OrderService) {
	await loadScript('https://static-na.payments-amazon.com/checkout.js', 'amazon', () => {
		registerAmazonGateways();
		setupAmazonPayButton();

		let previousGateway = '';
		let previousPage = Environment.modalUI.page();
		store.subscribe(async () => {
			if (DefaultCart.total() === 0) {
				return;
			}

			if (
				(PaymentConfiguration.selectedGateway() === 'peachpay_amazonpay' && previousGateway !== 'peachpay_amazonpay')
				|| (PaymentConfiguration.selectedGateway() === 'peachpay_amazonpay' && Environment.modalUI.page() === 'payment' && previousPage !== 'payment')
			) {
				previousGateway = 'peachpay_amazonpay';
				previousPage = 'payment';
				await initAmazonPayPaymentFlow(orderService);
			}

			if (PaymentConfiguration.selectedGateway() !== previousGateway) {
				previousGateway = PaymentConfiguration.selectedGateway();
			}

			if (Environment.modalUI.page() !== previousPage) {
				previousPage = Environment.modalUI.page();
			}
		});
	});
}

function initAmazonPayMethod(): AmazonGateway {
	return {
		config: {
			name: 'Amazon Pay',
			gatewayId: 'peachpay_amazonpay',
			description: 'Please click the Amazon Pay button to complete your checkout.',
			assets: {
				title: {src: amazonImageURL},
				badge: {src: amazonBadgeURL},
			},
		},
	};
}

function registerAmazonGateways(): Record<string, AmazonGateway> {
	const gatewayImplementations: Record<string, AmazonGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	const method = initAmazonPayMethod();
	gatewayImplementations[method.config.gatewayId] = method;
	gatewayConfigurations[method.config.gatewayId] = method.config;

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

async function initAmazonPayPaymentFlow(orderService: OrderService) {
	if (!GLOBAL.phpData?.plugin_asset_url) {
		return;
	}

	/**
	 * Clear old instances of aPay button since they contain customer shipping data which might have changed
	 * when modal state change
	 */
	$qsAll('.pp-amazon-pay-btn-container', $el => {
		const elId = $el.id;
		const $parentEl = $el.parentElement;
		$parentEl?.replaceChildren();
		if ($parentEl !== null) {
			$parentEl.innerHTML = '';
		}

		const htmlString = `<div id="${elId}" class=""></div>`;
		$parentEl?.insertAdjacentHTML('beforeend', htmlString);
	});

	$qs('#amazon-pay-btn-container')?.classList.add('pp-amazon-pay-btn-container');
	$qs('#amazon-pay-btn-container-mobile')?.classList.add('pp-amazon-pay-btn-container');

	// The URL Amazon Pay redirects to once the customer has completed checkout
	const returnURL = `${GLOBAL.phpData.plugin_asset_url}public/amazon-checkout.html`;
	// eslint-disable-next-line new-cap
	const amazonSession = await AmazonPaySession(returnURL);

	['#amazon-pay-btn-container', '#amazon-pay-btn-container-mobile'].forEach(elementID => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		const amazonPayButton = amazon.Pay.renderButton(elementID, {
			merchantId: amazonSession.merchantId,
			publicKeyId: amazonSession.publicKeyId,
			ledgerCurrency: MerchantConfiguration.currency.code(),
			checkoutLanguage: 'en_US',
			productType: Carts.virtual() ? 'PayOnly' : 'PayAndShip',
			placement: 'Cart',
			buttonColor: 'Gold',
			sandbox: amazonSession.sandbox,
		});

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		amazonPayButton.onClick(async () => {
			if (!GLOBAL.phpData?.plugin_asset_url) {
				return;
			}

			$qs('body')?.classList.add('pp-disabled');

			const popupURL = `${GLOBAL.phpData.plugin_asset_url}public/amazon-checkout.html`;
			await openAmazonPayPopup(amazonSession, popupURL, orderService);
		});
	});
}

async function openAmazonPayPopup(amazonSession: IAmazonPaySession, popupURL: string, orderService: OrderService) {
	const toFixed = MerchantConfiguration.currency.configuration().number_of_decimals;
	const amazonWindowDataBase64 = btoa(JSON.stringify({
		buttonData: {
			merchantId: amazonSession.merchantId,
			publicKeyId: amazonSession.publicKeyId,
			ledgerCurrency: MerchantConfiguration.currency.code(),
			checkoutLanguage: Environment.language().replace('-', '_'),
			productType: Carts.virtual() ? 'PayOnly' : 'PayAndShip',
			placement: 'Cart',
			buttonColor: 'Gold',
			sandbox: amazonSession.sandbox,
		},
		initCheckoutData: {
			createCheckoutSessionConfig: {
				payloadJSON: JSON.stringify(amazonSession.payload),
				signature: amazonSession.signature,
				publicKeyId: amazonSession.publicKeyId,
			},
		},
		scriptLink: getAmazonPayCheckoutScript(PeachPayCustomer.billing.country()),
	}));

	const popupWindow = createPopupWindow(`${popupURL}?amazon_data=${amazonWindowDataBase64}`, 'amazonPayPopup', window, 550, 515);

	const closedCheck = setInterval(() => {
		if (popupWindow?.closed) {
			clearInterval(closedCheck);
			$qs('body')?.classList.remove('pp-disabled');
		}
	}, 1000);

	if (popupWindow !== null) {
		const transaction = await orderService.startTransaction('peachpay_amazonpay');
		const orderResponse = await orderService.placeOrder(transaction);
		if (!transaction || orderResponse.result !== 'success') {
			return;
		}

		onWindowDataFetch('pp-amazon-popup-closed', () => {
			$qs('body')?.classList.remove('pp-disabled');
		});

		onWindowMessage('pp-amazon-checkout-failed', winMessage => {
			store.dispatch(stopModalLoading());

			// eslint-disable-next-line no-alert, @typescript-eslint/restrict-template-expressions
			window.alert(`Amazon Pay was unable to proceed with checkout due to the following:\n${winMessage.message}`);
			captureSentryException(new Error(winMessage.message), {
				amazonpay_session: amazonSession,
			});
		});

		onWindowMessage('pp-amazon-checkout-complete', async message => {
			const checkoutSessionId = (message as IAmazonWindowMessage).sessionId;
			popupWindow.postMessage({event: 'pp-amazon-received-session-id'}, location.origin);

			const request = {
				session: {
					id: PeachPayOrder.sessionId(),
					merchant_id: MerchantConfiguration.id(),
					merchant_url: MerchantConfiguration.hostName(),
					merchant_name: MerchantConfiguration.name(),
					plugin_version: Environment.plugin.version(),
					platform: 'woocommerce',
				},
				transaction: {
					amazonpay: {
						payload: {
							chargeAmount: {
								amount: (DefaultCart.total()).toFixed(toFixed),
								currencyCode: MerchantConfiguration.currency.code(),
							},
						},
						session_id: checkoutSessionId,
						sandbox: amazonSession.sandbox,
					},
				},
				order: {
					id: String(orderResponse.order_id),
					amount: (DefaultCart.total()).toFixed(toFixed),
					currency: MerchantConfiguration.currency.code(),
					data: orderResponse,
				},
			};

			const response = await fetch(
				getBaseURL(MerchantConfiguration.hostName(), Environment.testMode())
					+ 'api/v1/amazonpay/checkout',
				{
					method: 'POST',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify(request),
				},
			);

			if (!response.ok) {
				captureSentryException(new Error('Call to PeachPay API /amazonpay/checkout failed'), {
					amazonpay_session: amazonSession,
					request,
					response,
				});
				return;
			}

			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const result = await response.json();

			store.dispatch(startModalProcessing());
			await handleAmazonCompletion(result, checkoutSessionId, orderService, transaction, orderResponse);
		});
	}
}

// eslint-disable-next-line max-params
async function handleAmazonCompletion(
	result: any, // See https://developer.amazon.com/docs/amazon-pay-api-v2/checkout-session.html#response-3 for type
	checkoutSessionId: string,
	orderService: OrderService,
	transaction: ITransactionService,
	order: WCOrder,
) {
	// eslint-disable-next-line no-negated-condition
	if (result.data.statusDetails.state !== 'Completed') {
		store.dispatch(stopModalLoading());
	} else {
		await orderService.setOrderStatus(order, transaction, {
			amazonpay: {session_id: checkoutSessionId},
			paymentStatus: 'success',
			orderStatus: 'success',
		});

		store.dispatch(updateCustomerPreferredGateway(PaymentConfiguration.selectedGateway()));

		updateCustomerDataInBrowser();
		clearLocalSession();

		if (window.top) {
			window.top.location = order.redirect;
		}
	}
}
